import { useState, useRef, useEffect } from "react";
import { ChevronDown, School, Plus, Settings, Check } from "lucide-react";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";

interface SchoolProfileSwitcherProps {
  onOpenManagement: () => void;
  onOpenAddForm: () => void;
}

export default function SchoolProfileSwitcher({
  onOpenManagement,
  onOpenAddForm,
}: SchoolProfileSwitcherProps) {
  const { profiles, activeProfile, switchProfile } = useSchoolProfile();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (activeProfile === null && profiles.length === 0) {
    return (
      <button
        onClick={onOpenManagement}
        className="flex items-center gap-2 px-3 py-1.5 bg-(--bg-accent) hover:bg-(--bg-secondary) text-(--primary-color) rounded-lg border border-(--primary-color) border-dashed transition-all text-sm font-medium"
      >
        <Plus className="w-4 h-4" />
        Configurar Escola
      </button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-(--bg-secondary) hover:bg-(--bg-accent) text-(--text-primary) rounded-xl border border-(--border-color) transition-all group"
      >
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center text-white shrink-0"
          style={{
            backgroundColor: activeProfile?.color || "var(--primary-color)",
          }}
        >
          <School className="w-3.5 h-3.5" />
        </div>
        <div className="flex flex-col items-start min-w-0">
          <span className="text-xs font-bold truncate max-w-[120px]">
            {activeProfile?.name || "Selecionar Escola"}
          </span>
          <span className="text-[10px] text-(--text-tertiary) truncate max-w-[120px]">
            {activeProfile?.institution || "Perfil Professor"}
          </span>
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-(--text-tertiary) transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-(--card-bg) border border-(--border-color) rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-2 border-b border-(--border-color) bg-(--bg-secondary)">
            <span className="text-[10px] font-bold uppercase tracking-wider text-(--text-tertiary) px-2">
              Seus Perfis Escolares
            </span>
          </div>

          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => {
                  switchProfile(profile.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-(--bg-accent) transition-colors text-left group ${
                  activeProfile?.id === profile.id ? "bg-(--bg-accent)/50" : ""
                }`}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0 shadow-sm"
                  style={{
                    backgroundColor: profile.color || "var(--primary-color)",
                  }}
                >
                  <School className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-semibold truncate ${
                      activeProfile?.id === profile.id
                        ? "text-(--primary-color)"
                        : "text-(--text-primary)"
                    }`}
                  >
                    {profile.name}
                  </p>
                  <p className="text-xs text-(--text-tertiary) truncate">
                    {profile.institution || "Sem instituição"}
                  </p>
                </div>
                {activeProfile?.id === profile.id && (
                  <Check className="w-4 h-4 text-(--primary-color) shrink-0" />
                )}
              </button>
            ))}
          </div>

          <div className="p-2 border-t border-(--border-color) bg-(--bg-secondary)/50 space-y-1">
            <button
              onClick={() => {
                onOpenAddForm();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-(--text-secondary) hover:text-(--primary-color) hover:bg-(--bg-accent) rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Adicionar Escola
            </button>
            <button
              onClick={() => {
                onOpenManagement();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-(--text-secondary) hover:text-(--primary-color) hover:bg-(--bg-accent) rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
              Gerenciar Escolas
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
